(() => {
  const ROOT_SELECTOR =
    '[data-load-wrap]';

  const HERO_SELECTOR =
    '[data-hero-reveal]';

  const HERO_READY_EVENT =
    'wambolt:hero:initial-prepared';

  const LOADER_COMPLETE_EVENT =
    'wambolt:loader:complete';

  const DEPENDENCY_RETRY_LIMIT = 40;
  const DEPENDENCY_RETRY_DELAY = 100;

  const reducedMotionQuery =
    window.matchMedia(
      '(prefers-reduced-motion: reduce)'
    );

  let dependencyRetryTimer = null;
  let dependencyRetryAttempts = 0;

  let dependenciesRegistered = false;
  let initialized = false;
  let starting = false;
  let completionDispatched = false;


  /* =========================================
     DEPENDENCIES
     ========================================= */

  function registerDependencies() {
    if (
      !window.gsap ||
      !window.CustomEase ||
      !window.SplitText
    ) {
      return false;
    }

    if (!dependenciesRegistered) {
      gsap.registerPlugin(
        CustomEase,
        SplitText
      );

      CustomEase.create(
        'loader',
        '0.65, 0.01, 0.05, 0.99'
      );

      dependenciesRegistered = true;
    }

    return true;
  }


  /* =========================================
     INITIAL-LOAD PREFLIGHT

     The loader should animate only after cold-load
     font work and initial hero SplitText preparation
     have finished. This keeps expensive layout work
     out of the loader's visible moving frames.
     ========================================= */

  function waitForFonts() {
    if (
      !document.fonts ||
      !document.fonts.ready
    ) {
      return Promise.resolve();
    }

    return document.fonts.ready
      .catch(() => undefined);
  }


  function waitForInitialHero() {
    if (
      !document.querySelector(
        HERO_SELECTOR
      ) ||
      window.wamboltHeroInitialPrepared ===
        true
    ) {
      return Promise.resolve();
    }


    return new Promise(
      (resolve) => {
        let settled = false;
        let fallbackTimer = null;


        const finish = () => {
          if (settled) {
            return;
          }

          settled = true;

          window.removeEventListener(
            HERO_READY_EVENT,
            finish
          );

          if (fallbackTimer !== null) {
            window.clearTimeout(
              fallbackTimer
            );

            fallbackTimer = null;
          }

          resolve();
        };


        window.addEventListener(
          HERO_READY_EVENT,
          finish,
          {
            once: true,
          }
        );


        /*
         * Safety fallback: never leave the storefront
         * trapped behind a static loader if a separate
         * hero runtime fails unexpectedly.
         */
        fallbackTimer =
          window.setTimeout(
            finish,
            4000
          );
      }
    );
  }


  function waitForSettledFrames(
    count = 3
  ) {
    return new Promise(
      (resolve) => {
        let remaining =
          count;


        const next = () => {
          remaining -= 1;

          if (remaining <= 0) {
            resolve();
            return;
          }

          window.requestAnimationFrame(
            next
          );
        };


        window.requestAnimationFrame(
          next
        );
      }
    );
  }


  async function waitForAnimationWindow() {
    await Promise.all([
      waitForFonts(),
      waitForInitialHero(),
    ]);

    /*
     * Lenis and other layout systems use queued RAF
     * refreshes when fonts settle. Give those existing
     * measurements time to complete before the loader
     * starts moving.
     */
    await waitForSettledFrames(3);
  }


  /* =========================================
     CLEANUP
     ========================================= */

  function unlockDocument() {
    document.documentElement
      .classList.remove(
        'wambolt-site-loader-active'
      );
  }

  function dispatchLoaderComplete() {
    if (completionDispatched) {
      return;
    }

    completionDispatched = true;

    window.dispatchEvent(
      new CustomEvent(
        LOADER_COMPLETE_EVENT
      )
    );
  }


  function removeLoader(wrap) {
    unlockDocument();

    if (wrap) {
      wrap.style.display = 'none';

      /*
       * The loader exists only for the current
       * full document load. Barba handles all
       * subsequent client-side navigation.
       */
      wrap.remove();
    }

    dispatchLoaderComplete();
  }


  /* =========================================
     REDUCED MOTION
     ========================================= */

  function completeWithoutMotion(
    wrap
  ) {
    if (!wrap) {
      unlockDocument();
      return;
    }

    removeLoader(wrap);
  }


  /* =========================================
     LOGO REVEAL LOADER
     ========================================= */

  function initLogoRevealLoader() {
    if (initialized) {
      return true;
    }

    const wrap =
      document.querySelector(
        ROOT_SELECTOR
      );

    if (!wrap) {
      unlockDocument();

      return true;
    }

    if (
      reducedMotionQuery.matches
    ) {
      initialized = true;

      completeWithoutMotion(
        wrap
      );

      return true;
    }

    if (!registerDependencies()) {
      return false;
    }

    initialized = true;


    /* =========================================
       ELEMENTS
       ========================================= */

    const container =
      wrap.querySelector(
        '[data-load-container]'
      );

    const bg =
      wrap.querySelector(
        '[data-load-bg]'
      );

    const progressBar =
      wrap.querySelector(
        '[data-load-progress]'
      );

    const logo =
      wrap.querySelector(
        '[data-load-logo]'
      );

    const textElement =
      wrap.querySelector(
        '[data-load-text]'
      );

    const resetTargets =
      Array.from(
        wrap.querySelectorAll(
          '[data-load-reset]:not([data-load-text])'
        )
      );


    /* =========================================
       MAIN TIMELINE

       Approximate total:
       2.5 seconds.
       ========================================= */

    const loadTimeline =
      gsap.timeline({
        defaults: {
          ease: 'loader',
        },

        onComplete() {
          removeLoader(wrap);
        },
      });


    /* =========================================
       INITIAL STATE
       ========================================= */

    loadTimeline.set(
      wrap,
      {
        display: 'block',
      },
      0
    );


    /* =========================================
       LOGO + PROGRESS
       0.00 - 1.70
       ========================================= */

    loadTimeline.to(
      progressBar,
      {
        scaleX: 1,
        duration: 1.7,
      },
      0
    );

    loadTimeline.to(
      logo,
      {
        clipPath:
          'inset(0% 0% 0% 0%)',

        duration: 1.7,
      },
      0
    );


    /* =========================================
       CONTENT EXIT
       1.70 - 2.05
       ========================================= */

    loadTimeline.add(
      'hideContent',
      1.7
    );

    loadTimeline.to(
      container,
      {
        autoAlpha: 0,
        duration: 0.35,
      },
      'hideContent'
    );

    loadTimeline.to(
      progressBar,
      {
        scaleX: 0,

        transformOrigin:
          'right center',

        duration: 0.35,
      },
      'hideContent'
    );


    /* =========================================
       BACKGROUND EXIT
       1.70 - 2.50
       ========================================= */

    loadTimeline.to(
      bg,
      {
        yPercent: -101,
        duration: 0.8,
      },
      'hideContent'
    );


    /* =========================================
       RESET TARGETS
       ========================================= */

    if (resetTargets.length) {
      loadTimeline.set(
        resetTargets,
        {
          autoAlpha: 1,
        },
        0
      );
    }


    /* =========================================
       ALWAYS READY TEXT
       ========================================= */

    if (textElement) {
      const splitText =
        new SplitText(
          textElement,
          {
            type:
              'lines,chars',

            mask:
              'lines',
          }
        );

      gsap.set(
        splitText.chars,
        {
          autoAlpha: 0,
          yPercent: 125,
        }
      );

      gsap.set(
        textElement,
        {
          autoAlpha: 1,
        }
      );


      /* =========================================
         TEXT IN
         ========================================= */

      loadTimeline.to(
        splitText.chars,
        {
          autoAlpha: 1,
          yPercent: 0,

          duration: 0.5,

          stagger: {
            each: 0.025,
          },
        },
        0
      );


      /* =========================================
         TEXT OUT
         ========================================= */

      loadTimeline.to(
        splitText.chars,
        {
          autoAlpha: 0,
          yPercent: -125,

          duration: 0.3,

          stagger: {
            each: 0.015,
          },
        },
        1.35
      );
    }

    return true;
  }


  /* =========================================
     DEPENDENCY-SAFE START
     ========================================= */

  function clearDependencyRetry() {
    if (
      dependencyRetryTimer === null
    ) {
      return;
    }

    window.clearTimeout(
      dependencyRetryTimer
    );

    dependencyRetryTimer = null;
  }

  function scheduleDependencyRetry() {
    if (
      dependencyRetryTimer !== null
    ) {
      return;
    }

    if (
      dependencyRetryAttempts >=
      DEPENDENCY_RETRY_LIMIT
    ) {
      console.warn(
        'Wambolt Site Loader could not initialize because GSAP, CustomEase, or SplitText is unavailable.'
      );

      removeLoader(
        document.querySelector(
          ROOT_SELECTOR
        )
      );

      return;
    }

    dependencyRetryAttempts += 1;

    dependencyRetryTimer =
      window.setTimeout(
        () => {
          dependencyRetryTimer = null;

          start();
        },
        DEPENDENCY_RETRY_DELAY
      );
  }

  async function start() {
    if (
      initialized ||
      starting
    ) {
      return;
    }


    if (
      !registerDependencies()
    ) {
      scheduleDependencyRetry();
      return;
    }


    starting = true;


    try {
      await waitForAnimationWindow();

      if (
        initLogoRevealLoader()
      ) {
        dependencyRetryAttempts = 0;

        clearDependencyRetry();
      } else {
        scheduleDependencyRetry();
      }
    } finally {
      starting = false;
    }
  }


  /* =========================================
     FULL DOCUMENT LOAD ONLY
     ========================================= */

  if (
    document.readyState ===
    'loading'
  ) {
    document.addEventListener(
      'DOMContentLoaded',
      start,
      {
        once: true,
      }
    );
  } else {
    start();
  }
})();